import { useState } from 'react';

import { TriangleAlertIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface QuickBooksWarningsIndicatorProps {
  messages: string[];
}

/** Warning icon (plus a count when there are several warnings), listing every message in a hover popover. */
export function QuickBooksWarningsIndicator({ messages }: QuickBooksWarningsIndicatorProps) {
  const [open, setOpen] = useState(false);

  if (messages.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className="inline-flex shrink-0 items-center gap-1 outline-none"
        onClick={event => event.stopPropagation()}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <TriangleAlertIcon size={16} className="shrink-0 text-warning" />
        {/* A single warning is fully conveyed by the icon; the count only helps when the row
            has several problems to fix (e.g. unlinked AND currency mismatch). */}
        {messages.length > 1 && (
          <Badge
            variant="warning"
            className="h-4 min-w-4 justify-center rounded-full px-1 text-[10px]"
          >
            {messages.length}
          </Badge>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-auto max-w-xs p-3"
        align="start"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <ul className="flex flex-col gap-1.5 text-sm">
          {messages.map(message => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
